import type { OpenAPISchemaProperty } from '../types/types';

/**
 * Merges multiple schemas together (for allOf composition)
 */
const mergeSchemas = (schemas: OpenAPISchemaProperty[]): OpenAPISchemaProperty => {
  const merged: OpenAPISchemaProperty = {
    type: 'object',
    properties: {},
  };

  schemas.forEach((schema) => {
    // Merge type with validation - only set if not already set or if it matches
    if (schema.type) {
      // Silently use the last defined type (typically all schemas in allOf have the same type anyway)
      merged.type = schema.type;
    }

    // Merge properties
    if (schema.properties) {
      merged.properties = {
        ...merged.properties,
        ...schema.properties,
      };
    }

    // Merge items for array types
    if (schema.items) {
      merged.items = schema.items;
    }

    // Merge required fields
    if (schema.required) {
      merged.required = [...(merged.required || []), ...schema.required];
    }

    // Copy other schema properties from the last schema that has them
    if (schema.example !== undefined) merged.example = schema.example;
    if (schema.default !== undefined) merged.default = schema.default;
  });

  return merged;
};

/**
 * Generates example JSON data from an OpenAPI schema
 * @param schema - The OpenAPI schema to generate an example from
 * @param depth - Current recursion depth to prevent infinite loops
 * @param maxDepth - Maximum recursion depth allowed
 * @returns Generated example data matching the schema structure
 */
export const generateExampleFromSchema = (
  schema: OpenAPISchemaProperty | undefined,
  depth = 0,
  maxDepth = 5,
): unknown => {
  if (!schema || depth > maxDepth) {
    return null;
  }

  // Handle $ref if it somehow wasn't dereferenced
  if ('$ref' in schema && typeof schema.$ref === 'string') {
    // Can't resolve refs here, return null
    return null;
  }

  // If the schema has an explicit example, use it
  if (schema.example !== undefined) {
    return schema.example;
  }

  // If the schema has a default value, use it
  if (schema.default !== undefined) {
    return schema.default;
  }

  // Handle allOf - merge all schemas together
  if (schema.allOf && Array.isArray(schema.allOf) && schema.allOf.length > 0) {
    const mergedSchema = mergeSchemas(schema.allOf as OpenAPISchemaProperty[]);
    return generateExampleFromSchema(mergedSchema, depth, maxDepth);
  }

  // Handle oneOf - use the first schema
  if (schema.oneOf && Array.isArray(schema.oneOf) && schema.oneOf.length > 0) {
    return generateExampleFromSchema(schema.oneOf[0] as OpenAPISchemaProperty, depth, maxDepth);
  }

  // Handle anyOf - use the first schema
  if (schema.anyOf && Array.isArray(schema.anyOf) && schema.anyOf.length > 0) {
    return generateExampleFromSchema(schema.anyOf[0] as OpenAPISchemaProperty, depth, maxDepth);
  }

  // If no type is specified but we have properties, treat as object
  if (!schema.type && schema.properties) {
    schema.type = 'object';
  }

  // If no type is specified but we have items, treat as array
  if (!schema.type && schema.items) {
    schema.type = 'array';
  }

  switch (schema.type) {
    case 'object':
      if (schema.properties) {
        const exampleObj: Record<string, unknown> = {};
        Object.entries(schema.properties).forEach(([key, prop]) => {
          const value = generateExampleFromSchema(prop as OpenAPISchemaProperty, depth + 1, maxDepth);
          exampleObj[key] = value;
        });
        return exampleObj;
      }
      return {};

    case 'array':
      if (schema.items) {
        const itemExample = generateExampleFromSchema(schema.items as OpenAPISchemaProperty, depth + 1, maxDepth);

        // Respect minItems if specified, using nullish coalescing to preserve valid zero values
        const minItems = (schema.minItems as number | undefined) ?? 1;
        const exampleArray: unknown[] = [];
        for (let i = 0; i < minItems; i++) {
          exampleArray.push(itemExample);
        }
        return exampleArray;
      }
      return [];

    case 'string':
      if (schema.enum && schema.enum.length > 0) {
        return schema.enum[0];
      }
      if (schema.format === 'date-time') {
        return '2024-01-01T00:00:00Z';
      }
      if (schema.format === 'date') {
        return '2024-01-01';
      }
      if (schema.format === 'email') {
        return 'user@example.com';
      }
      if (schema.format === 'uri' || schema.format === 'url') {
        return 'https://example.com';
      }
      return 'string';

    case 'number':
      return 0;

    case 'integer':
      return 0;

    case 'boolean':
      return true;

    default:
      // If type is not recognized, return null
      return null;
  }
};
